import React, { useEffect, useState } from "react"
import { makeStyles } from "@mui/styles"
import { InputAdornment, Stack, TextField, Typography } from "@mui/material"
import { handlePSEWeighingMergedError } from "../../../utils/productionStepExecution"
import { COLORS, roundNumber } from "../../../utils"

const useStyles = makeStyles({
  root: {
    border: "1px solid #E6E6E6",
    padding: "24px",
    gap: "24px",
    borderRadius: "8px"
  },
  label: {
    color: "#262626",
    fontSize: 20,
    fontWeight: 500,
    lineHeight: "28px"
  },
  icon: {
    width: 24,
    height: 24
  },
  iconContainer: {
    height: 52,
    width: 52,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#DBDBDB",
    borderRadius: "8px",
  },
  titleContainer: {
    display: "flex",
    flex: "1 1",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8
  },
  title: {
    color: "#555",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "22px"
  },
  instructions: {
    color: "#262626",
    fontSize: "18px",
    fontWeight: 400,
    lineHeight: "24px"
  },
  singleValueContainer: {
    display: "flex",
  },
  blocMachine: {
    display: "flex",
    flexDirection: "row",
    flex: 1
  },
  fieldValue: {
    fontFamily: "Roboto",
    color: "#262626",
    fontWeight: 400,
    fontSize: 18,
    lineHeight: "24px",
    flexDirection: "row",
    composes: "flexRow"
  },
  subBlocMachine1: {
    flex: 1
  },
  weightsBlock: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 16,
    marginLeft: 24,
    flexShrink: 0
  },
  stackStyle: {
    width: "1px",
    backgroundColor: "#E6E6E6",
    alignSelf: "stretch",
    margin: "0 8px"
  },
  error: {
    color: "#F44259"
  },
  sectionField: {
    color: COLORS.DRAFT_GREY,
    margin: 0,
    fontWeight: 400
  },
})

const TextFieldWithAdornment = (props) => {
  const isExceeded = props.max && roundNumber(parseFloat(props.value || 0), 2) > roundNumber(parseFloat(props.max), 2)
  return (
    <TextField
      {...props}
      InputProps={{
        endAdornment: (
          props.value ? <InputAdornment position="end" sx={{ "& *": { color: COLORS.BLACK800 } }}>Kg</InputAdornment> : null
        )
      }}
      sx={{
        "& .MuiInput-underline:after": {
          borderColor: isExceeded ? "red !important" : "inherit"
        },
        "& .MuiInput-underline:before": {
          borderColor: isExceeded ? "red !important" : "inherit"
        }
      }}
    />
  )
}

const PSEMergedWeighing = ({ productionStepExecution, hasWeightMergedFormErrors, setFieldValue, values }) => {
  const classes = useStyles()
  const mergedProductionStepExecutions = productionStepExecution.mergedProductionStepExecutions || []
  const [theoreticalNetWeight, setTheoreticalNetWeight] = useState([])
  
  const totalTheoreticalNetWeight = mergedProductionStepExecutions.reduce((sum, pse) => sum + (pse.theoreticalNetWeight || 0), 0)
  const netWeightPese = values?.netWeight ?? totalTheoreticalNetWeight
  const [pseTheoreticalNetWeightFilled, setPSETheoreticalNetWeightFilled] = useState(0)
  const [netWeightMergedError, setNetWeightMergedError] = useState(null)
  const sectionName = productionStepExecution.mergedProductionStepExecutions && productionStepExecution.mergedProductionStepExecutions[0].sectionName

  useEffect(() => {
    const netWeightMergedError = handlePSEWeighingMergedError(theoreticalNetWeight, pseTheoreticalNetWeightFilled, netWeightPese)
    setNetWeightMergedError(netWeightMergedError)
  }, [pseTheoreticalNetWeightFilled, theoreticalNetWeight, hasWeightMergedFormErrors, netWeightPese])

  return (
    <Stack>
      <Stack className={classes.root}>
        <Stack direction="row" spacing={1} alignItems="center">
          <div className={classes.iconContainer}>
            <img className={classes.icon} src="/img/recipes/balance.svg" />
          </div>
          <span className={classes.label}>Répartition des poids par recette</span>
        </Stack>
        {mergedProductionStepExecutions.map(pse => {
          return (
            <div className={classes.blocMachine} key={pse.objectId}>
              <div className={classes.subBlocMachine1}>
                <div className={classes.titleContainer}>
                  <div className={classes.title}>
                    Recette
                  </div>
                </div>
                <div className={classes.singleValueContainer}>
                  <span className={classes.fieldValue}>
                    <b>{pse.uniqueCode}&nbsp;</b>-&nbsp;{pse.recipeName}
                    <p className={classes.sectionField}>&nbsp;({sectionName})</p>
                  </span>
                </div>
              </div>
              <div className={classes.weightsBlock}>
                <Stack sx={{ minWidth: 80, height: 64, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <Typography sx={{ color: COLORS.BLACK_600, fontSize: "16px", lineHeight: "24px" }}>Poids prévu</Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: "16px", lineHeight: "24px" }}>
                    {roundNumber(pse.theoreticalNetWeight, 2)} kg
                  </Typography>
                </Stack>
                <div style={{ width: "32px" }} />
                <Stack sx={{ width: 107, height: 64, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <Typography sx={{ color: COLORS.BLACK_600, fontSize: "16px", lineHeight: "24px" }}>Poids pesé</Typography>
                  <TextFieldWithAdornment
                    variant="standard"
                    name="netWeightMerged"
                    max={pse.theoreticalNetWeight}
                    inputProps={{
                      inputMode: "decimal",
                      pattern: "[0-9]*[.,]?[0-9]*"
                    }}
                    value={(() => {
                      const idPSE = pse.productionStepExecution?.objectId
                      const entry = theoreticalNetWeight.find(obj => obj.idPSE === idPSE)
                      return entry ? entry.displayValue ?? "" : ""
                    })()}
                    onChange={(e) => {
                      const raw = e.target.value.replace(",", ".")
                      const idPSE = pse.productionStepExecution.objectId

                      // Keep the raw string for display (so "1." doesn't get stripped to "1")
                      // but parse the numeric value separately for form validation
                      const numericValue = raw === "" ? null : parseFloat(raw)

                      const updatedTheoreticalNetWeight = [...theoreticalNetWeight]
                      const index = updatedTheoreticalNetWeight.findIndex(obj => obj.idPSE === idPSE)

                      if (raw === "") {
                        if (index !== -1) updatedTheoreticalNetWeight.splice(index, 1)
                      } else {
                        const entry = { idPSE, value: isNaN(numericValue) ? null : numericValue, displayValue: raw }
                        if (index !== -1) updatedTheoreticalNetWeight[index] = entry
                        else updatedTheoreticalNetWeight.push(entry)
                      }

                      setTheoreticalNetWeight(updatedTheoreticalNetWeight)
                      setFieldValue("netWeightMerged", updatedTheoreticalNetWeight)

                      const pseTheoreticalNetWeightFilled = updatedTheoreticalNetWeight
                        .map(obj => obj.value || 0)
                        .reduce((sum, current) => sum + current, 0)

                      setPSETheoreticalNetWeightFilled(pseTheoreticalNetWeightFilled)
                    }}
                    type="text"
                    onWheel={(e) => e.target?.blur()}
                  />
                </Stack>
              </div>
            </div>
          )
        })}
        <Stack spacing={3} sx={{ fontSize: 14, fontWeight: 400, flexDirection: "row", composes: "flexRow", justifyContent: "flex-end", alignSelf: "flex-end", alignItems: "flex-end" }}>
          Poids total attribué :
          <span className={pseTheoreticalNetWeightFilled !== 0 && roundNumber(pseTheoreticalNetWeightFilled, 2) !== roundNumber(netWeightPese, 2) ? classes.error : null}>&nbsp;{roundNumber(pseTheoreticalNetWeightFilled, 2)}&nbsp;</span>/ {roundNumber(netWeightPese, 2)} kg
        </Stack>
      </Stack>
      {netWeightMergedError &&
        (
          <Stack sx={{ color: "#F44259", pt: 2 }}>
            {netWeightMergedError}
          </Stack>
        )
      }
    </Stack>
  )
}

export default PSEMergedWeighing
